import translate from '@translate';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import getBackUrl from 'src/utils/getBackUrl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { OrderProductSetWithProductType, OrderType } from 'src/types/order';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import {
    buildOrderCancelPatchPayload,
    isCancelStatusChange,
    isEmptyCancelReason,
    isOrderCancelReasonRequired,
    ORDER_CANCEL_STATUS,
} from 'src/utils/cancelStatus';
import Loading from 'src/components/Loading/intex';
import { checkPermission, useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import { EDIT_KEYS, EditKeysType, INIT_TAB, STORAGE_KEY, URL_NEW } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import api from '@api';
import {
    clearOrderDetail,
    createInvoiceOrderDetailFetch,
    createOrderDetailFetch,
    editOrderDetailEshopNoteFetch,
    getOrderDetailChangelogFetch,
    getOrderDetailClaimsReturnsFetch,
    getOrderDetailExpeditionPackagesFetch,
    getOrderDetailFetch,
    getOrderDetailFilesFetch,
    getOrderDetailProductsFetch,
    getOrderDetailProductsFetchSuccess,
    getOrderDetailProductsFilesFetch,
    getOrderDetailTransportHistoryFetch,
    getOrderDetailTransportsFetch,
    getOrderPalletsFetch,
    patchOrderDetailFetch,
    updateOrderDetailFetch,
} from 'src/resources/orderDetail/reducer';
import { orderUsesPalletsTab } from './showsOrderPalletsTab';
import RenderComponents from './RenderComponents';
import {
    formatIncorrectMessage,
    getAllowedStatuses,
    getChangesProviderCompanies,
    getCloneOrder,
    getCloneProductsForSets,
    getSplitOrder,
    buildOrderSavePayload,
    ensureOrderHasFiles,
    ensureOrderHasProducts,
    mergeLazyOrderDetailFields,
    mergeOrderDetailAfterServerRefresh,
    resolveProductsForSets,
    isDisableEditPrintFiles,
    isDisablePaidActions,
    isDisableSplit,
    isSameRecipientBilling,
} from './functions';
import ShipperLabel from './ShipperLabel';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import ProviderCompanySelector from 'src/components/ProviderCompanySelector';
import { getProviderCompaniesListFetch } from 'src/resources/providerCompaniesList/reducer';
import { CREATE_CLAIM_STATUSES, CREATE_RETURN_STATUSES } from '../AllOrdersContainer/constants';
import { getOrderClaims } from '../AllOrdersContainer/functions';
import { buildClaimExistsDialogParams } from '../ClaimDetailContainer/claimExistsDialog';
import { reorderOrderShippingFetch } from 'src/resources/ordersUpdate/reducer';
import { setStorageData } from 'src/resources/storage/reducer';
import isDeepEqual from 'src/utils/isDeepEqual';
import OrderProductDetail from './OrderProductDetail';
import ReleaseFromSuspendedConfirm, { ReleaseFromSuspendedConfirmDataType } from './ReleaseFromSuspendedConfirm';
import { FormErrorsType, OnChangeType } from '../../types/global';
import moment from 'moment';
import { getRequiredExpeditionDateError, isRequiredExpeditionDateEditable } from './requiredExpeditionDate';
import { DateType } from 'src/types/date';
import { SOURCE_SYS_MYSKLADON } from 'skladon-types';

export type OnChangeMultipleType = (orderPart: OrderType) => void;

const OrderDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { idOrder } = useParams();

    const newOrder = !idOrder;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.orderDetail.data);
    const detailFetchRevision = useSelector(
        (state: RootStoreType) => state.orderDetail.detailFetchRevision
    );
    const changelog = useSelector((state: RootStoreType) => state.orderDetail.changelog);
    const expeditionPackages = useSelector((state: RootStoreType) => state.orderDetail.expeditionPackages);
    const orderPallets = useSelector((state: RootStoreType) => state.orderDetail.orderPallets);
    const transportHistory = useSelector((state: RootStoreType) => state.orderDetail.transportHistory);
    const orderTransports = useSelector((state: RootStoreType) => state.orderDetail.orderTransports);
    const claimsReturns = useSelector((state: RootStoreType) => state.orderDetail.claimsReturns);
    const loading = useSelector((state: RootStoreType) => state.orderDetail.loading);
    const loadingUpdate = useSelector((state: RootStoreType) => state.ordersUpdate.loading);
    const providerData = useSelector((state: RootStoreType) => state.provider.data);
    const orderStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.orderStatusCodes);
    const orderMarketplacesCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.orderMarketplacesCodes
    );
    const orderStatusCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.orderStatusCodes);
    const orderMarketplacesCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.orderMarketplacesCodes
    );
    const orderCancelReasonCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.orderCancelReasonCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const boolNumberCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.boolNumberCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);
    const paymentCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.paymentCodes);
    const paymentCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.paymentCodes);
    const documentTypesCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.documentTypesCodes);
    const orderProductFileTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.orderProductFileTypeCodes
    );
    const orderProductCustomizationTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.orderProductCustomizationTypeCodes
    );
    const orderProductCustomizationTypeCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.orderProductCustomizationTypeCodes
    );
    const shipperCodes = useSelector((state: RootStoreType) =>
        [...state.constantsCodes.data.shipperCodes].sort((a, b) => a.label.localeCompare(b.label))
    );
    const orderCancelReasonCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.orderCancelReasonCodes
    );
    const shipperCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.shipperCodes);
    const transportStatusCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.transportStatusCodes
    );
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const serviceCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.serviceCodes);
    const orderProductCustomsTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.orderProductCustomsTypeCodes
    );
    const shipperMessageCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.shipperMessageCodes);
    const orderProductCustomsTypeCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.orderProductCustomsTypeCodes
    );
    const lineStateCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.lineStateCodes);
    const orderTypeCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.orderTypeCodes);
    const sourceSysCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.sourceSysCodes);
    const orderClaimReturnTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.orderClaimReturnTypeCodes
    );
    const claimStatusCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.claimStatusCodes);
    const returnStatusCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.returnStatusCodes
    );
    const providerServiceCodes = useSelector((state: RootStoreType) => state.user.serviceCodes);
    const skladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const providerCompaniesData = useSelector((state: RootStoreType) => state.providerCompaniesList.data);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);

    const initData = {
        METHOD: 'DEL',
        PRINT_FILES: providerData?.DEFAULT_PRINT_FILES,
        ALLOW_EXPEDITION: providerData?.DEFAULT_ALLOW_EXPEDITION,
        FILES: [],
        ORDER_PRODUCTS: [],
        PAID: 0,
        SOURCE_SYS: SOURCE_SYS_MYSKLADON,
    };

    const providerAllowedOrderProductsEdit = !!providerData?.ALLOWED_ORDER_PRODUCTS_EDIT;
    const [editData, setEditData] = useState<OrderType>(getEditData(initData, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [activeTab, setActiveTab] = useState<string>(INIT_TAB);
    const [showShipperLabel, setShowShipperLabel] = useState<
        'shipResponse' | 'shipReturnResponse' | 'shipSwapResponse' | false
    >(false);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [sameRecipientBilling, setSameRecipientBilling] = useState<boolean>(true);
    const [incorrectMessage, setIncorrectMessage] = useState<string>('');
    const [productsForSets, setProductsForSets] = useState<OrderProductSetWithProductType[]>([]);
    const [openProductDetailId, setOpenProductDetailId] = useState<number | null>(null);
    const filesFetchedRef = useRef(false);
    const productsFilesFetchedRef = useRef(false);
    const productsFetchedRef = useRef(false);
    const expeditionPackagesFetchedRef = useRef(false);
    const transportHistoryFetchedRef = useRef(false);
    const claimsReturnsFetchedRef = useRef(false);
    const orderTransportsFetchedRef = useRef(false);
    const loadedOrderIdRef = useRef<number | null>(null);
    const syncedDetailFetchRevisionRef = useRef(0);

    const permissionCreate = useCheckPermission('ORDER.CREATE');
    const permissionUpdate = useCheckPermission('ORDER.UPDATE');
    const permissionTransportList = useCheckPermission('TRANSPORT.LIST');
    const permissionTransportCreate = useCheckPermission('TRANSPORT.CREATE');
    const permissionEditAll = useCheckPermission('ORDER.EDITALL');
    const permissionManagerEdit = useCheckPermission('ORDER.MANAGER_EDIT');
    const permissionOrderProductFilesEdit = useCheckPermission('ORDER.ORDER_PRODUCTS.FILES.EDIT');
    const permissionEditCurrentStatus =
        data?.STATUS && checkPermission(`ORDER.STATUS.EDIT.${data.STATUS.toUpperCase()}`);

    const orderPropertyPermissions = useMemo(() => {
        const statusUpper = data?.STATUS?.toUpperCase();
        if (!statusUpper) return {} as Record<string, boolean>;
        return EDIT_KEYS.reduce(
            (acc, key) => {
                acc[key] = checkPermission(`ORDER.PROPERTIES.${key}.EDIT.${statusUpper}`);
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [data?.STATUS]);

    const permissionReorderShipping =
        !!data?.ORDER_TYPE &&
        !!data?.STATUS &&
        checkPermission(`ORDER.ACTION.SHIPPING.STATUS.${data.ORDER_TYPE.toUpperCase()}.${data.STATUS.toUpperCase()}`);
    const permissionEditAddress = !!data?.STATUS &&
        checkPermission(`ORDER.PROPERTIES.ADDRESS.EDIT.${data.STATUS.toUpperCase()}`);
    const permissionCreateClaim = useCheckPermission('CLAIM.CREATE');
    const permissionCreateMultipleClaims = useCheckPermission('CLAIM.CREATE_MULTIPLE');
    const permissionCreateReturn = useCheckPermission('RETURN.CREATE');
    const permissionOrderPalletsList = useCheckPermission('ORDER_PALLETS.LIST');
    const permissionOrderPalletsCreate = useCheckPermission('ORDER_PALLETS.CREATE');
    const permissionOrderPalletsUpdate = useCheckPermission('ORDER_PALLETS.UPDATE');
    const disableEditPrintFiles = isDisableEditPrintFiles(data, orderStatusCodesObject);
    const disableEditAllowExpedition = isDisableEditPrintFiles(data, orderStatusCodesObject);
    const disableSplit = !permissionCreate || data?.STATUS !== 'productsNotAvailable' || isDisableSplit(data);
    const showRemoveUnavailableProducts =
        !disableSplit &&
        (providerAllowedOrderProductsEdit || data?.SOURCE_SYS === SOURCE_SYS_MYSKLADON);
    const disableCreateClaim = !permissionCreateClaim || !CREATE_CLAIM_STATUSES.includes(data?.STATUS || '');
    const disableCreateReturn = !permissionCreateReturn || !CREATE_RETURN_STATUSES.includes(data?.STATUS || '');
    const disablePaidActions = isDisablePaidActions(data, orderStatusCodesObject, newOrder);
    const canCreateOrderTransport = permissionTransportCreate;

    const disableEdit =
        !permissionEditAll && (newOrder ? !permissionCreate : !permissionEditCurrentStatus || !permissionUpdate);
    const isAttributeEditDisabled = (attributeKey: string) =>
        Boolean(disableEdit && !permissionEditAll && !orderPropertyPermissions[attributeKey]);
    const canEditAnyOrderPropertyInCurrentStatus = Object.values(orderPropertyPermissions).some(Boolean);
    const canEditPalletsInCurrentStatus =
        permissionEditAll || permissionEditCurrentStatus || !isAttributeEditDisabled('ORDER_PALLETS');
    const allowedStatuses = getAllowedStatuses(orderStatusCodes, orderStatusCodesObject, data);

    const backUrl = getBackUrl(locationHistory, '/orders/', '/orders/new', [/^\/orders\/\d+\/?$/]);

    const orderType = (data?.ORDER_TYPE || '').toUpperCase();

    // save edit data -start
    const editDataRef = useRef<OrderType>({});
    useEffect(() => {
        if (newOrder && storageData) {
            setConfirmParams({
                title: translate('orderDetail.confirmGetNewDataStorage'),
                content: translate('orderDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                },
            });
        }
        return () => {
            if (!editDataRef.current?.ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const setThisStorageData = (data: OrderType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };
    // save edit data -end

    useEffect(() => {
        if (idOrder) {
            dispatch(setLocationParams({ name: translate('orderDetail.title', { idOrder, orderType }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('orderDetail.titleNew'), backUrl }));
        }
    }, [idOrder, orderType, backUrl, locationHistory]);

    useEffect(() => {
        if (idOrder) {
            dispatch(getOrderDetailFetch(idOrder));
        }
        filesFetchedRef.current = false;
        productsFilesFetchedRef.current = false;
        productsFetchedRef.current = false;
        expeditionPackagesFetchedRef.current = false;
        transportHistoryFetchedRef.current = false;
        claimsReturnsFetchedRef.current = false;
        orderTransportsFetchedRef.current = false;
        loadedOrderIdRef.current = null;
        syncedDetailFetchRevisionRef.current = 0;
        return () => {
            dispatch(clearOrderDetail());
        };
    }, [idOrder]);

    useEffect(() => {
        if (orderPallets) {
            setEditData({ ...editData, ORDER_PALLETS: orderPallets });
        }
    }, [orderPallets]);

    useEffect(() => {
        if (!data) {
            return;
        }

        const isInitialLoad = loadedOrderIdRef.current !== data.ID;
        const hasNewDetailFetch =
            syncedDetailFetchRevisionRef.current !== detailFetchRevision;
        const shouldFullSync = isInitialLoad || hasNewDetailFetch;

        if (shouldFullSync) {
            syncedDetailFetchRevisionRef.current = detailFetchRevision;
            loadedOrderIdRef.current = data.ID ?? null;
            if (isInitialLoad) {
                setEditData(data);
                setEditErrors({});
                setActiveTab(INIT_TAB);
            } else {
                setEditData((prev) => mergeOrderDetailAfterServerRefresh(prev, data));
            }
            setSameRecipientBilling(isSameRecipientBilling(data));
            setIncorrectMessage(formatIncorrectMessage(data, shipperMessageCodes));

            // redirect after create
            if (isInitialLoad) {
                if (data.ID !== Number(idOrder)) {
                    navigate('/orders/' + String(data.ID));
                    setThisStorageData(null);
                } else if (location.pathname.split('/')[3] === 'clone') {
                    handleClone();
                }
            }
            return;
        }

        setEditData((prev) => mergeLazyOrderDetailFields(prev, data));
    }, [data, detailFetchRevision]);

    useEffect(() => {
        if (!idOrder) {
            return;
        }
        if (!changelog && activeTab === 'history') {
            dispatch(getOrderDetailChangelogFetch(idOrder));
        }
        if (activeTab === 'transport' && data && data.FILES === undefined && !filesFetchedRef.current) {
            filesFetchedRef.current = true;
            dispatch(getOrderDetailFilesFetch(idOrder));
        }
        // PNA orders need products on Transport so Split button visibility can be computed.
        if (
            data?.STATUS === 'productsNotAvailable' &&
            data.ORDER_PRODUCTS === undefined &&
            !productsFetchedRef.current
        ) {
            productsFetchedRef.current = true;
            dispatch(getOrderDetailProductsFetch(idOrder));
        }
        if (activeTab === 'products' && data && !productsFetchedRef.current && data.ORDER_PRODUCTS === undefined) {
            productsFetchedRef.current = true;
            dispatch(getOrderDetailProductsFetch(idOrder));
        }
        if (
            activeTab === 'products' &&
            providerData?.ALLOW_ORDER_PRODUCT_DETAIL &&
            data?.ORDER_PRODUCTS &&
            !productsFilesFetchedRef.current
        ) {
            productsFilesFetchedRef.current = true;
            dispatch(getOrderDetailProductsFilesFetch(data.ID));
        }
        if (activeTab === 'expeditionPackages' && !expeditionPackagesFetchedRef.current) {
            expeditionPackagesFetchedRef.current = true;
            dispatch(getOrderDetailExpeditionPackagesFetch(idOrder));
        }
        if (activeTab === 'transportHistory' && !transportHistoryFetchedRef.current) {
            transportHistoryFetchedRef.current = true;
            dispatch(getOrderDetailTransportHistoryFetch(idOrder));
        }
        if (
            permissionTransportList &&
            !orderTransportsFetchedRef.current &&
            activeTab === 'transports'
        ) {
            orderTransportsFetchedRef.current = true;
            dispatch(getOrderDetailTransportsFetch(idOrder));
        }
        if (activeTab === 'claimsReturns' && !claimsReturnsFetchedRef.current) {
            claimsReturnsFetchedRef.current = true;
            dispatch(getOrderDetailClaimsReturnsFetch(idOrder));
        }
        if (activeTab === 'pallets' && permissionOrderPalletsList) {
            // Always load pallets when switching to the pallets tab
            dispatch(getOrderPalletsFetch(idOrder));
        }
        if (activeTab === 'products' && data?.ORDER_PRODUCT_SETS) {
            loadProductForSets();
        }
    }, [
        activeTab,
        changelog,
        expeditionPackages,
        transportHistory,
        claimsReturns,
        data,
        idOrder,
        permissionTransportList,
    ]);

    // load provider companies
    useEffect(() => {
        if (!providerCompaniesData) {
            dispatch(getProviderCompaniesListFetch());
        }
    }, [providerCompaniesData]);

    const loadProductForSets = async () => {
        if (data?.ORDER_PRODUCT_SETS) {
            const newProductsForSets = await resolveProductsForSets(data.ORDER_PRODUCT_SETS);
            setProductsForSets(newProductsForSets);
        } else {
            setProductsForSets([]);
        }
    };

    const onChangeMultiple: OnChangeMultipleType = (orderPart) => {
        const newData = { ...editData, ...orderPart };
        const newErrors = { ...editErrors };
        Object.keys(orderPart).forEach((key) => {
            delete newErrors[key];
        });
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const onSetProviderCompany = (type: 'recipient' | 'billing') => {
        setConfirmParams({
            title: translate(`orderDetail.selectProviderCompany${type}`),
            customContent: (confirmData: any, setConfirmData: React.Dispatch<any>) => {
                return (
                    <ProviderCompanySelector
                        data={confirmData}
                        setData={setConfirmData}
                        providerCompaniesData={providerCompaniesData || []}
                    />
                );
            },
            action: (confirmData: any) => {
                if (!Object.keys(confirmData).length) {
                    return;
                }
                onChangeMultiple(getChangesProviderCompanies(confirmData, type));
            },
        });
    };

    const handleRemoveUnavailableProducts = () => {
        setConfirmParams({
            title: translate(`orderDetail.action.removeUnavailableProducts`),
            content: translate(`orderDetail.action.removeUnavailableProductsText`),
            action: async () => {
                if (!data || !idOrder) {
                    return;
                }
                const orderWithProducts = await ensureOrderHasProducts(data, idOrder);
                if (data.ORDER_PRODUCTS === undefined) {
                    dispatch(
                        getOrderDetailProductsFetchSuccess({
                            ORDER_PRODUCTS: orderWithProducts.ORDER_PRODUCTS,
                            ORDER_PRODUCT_SETS: orderWithProducts.ORDER_PRODUCT_SETS,
                        })
                    );
                    productsFetchedRef.current = true;
                }
                const { updateOrder } = getSplitOrder(orderWithProducts);

                dispatch(updateOrderDetailFetch({ idOrder, data: updateOrder }));
            },
        });
    };

    const onChangeProductsForSets = (value: OrderProductSetWithProductType[]) => {
        setProductsForSets(value);
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error, errorKey, clearErrors) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;

        if (clearErrors && key === 'ORDER_PALLETS') {
            // If clearErrors is true and we're updating ORDER_PALLETS, clear all ORDER_PALLETS related errors
            Object.keys(newErrors).forEach((errorKey) => {
                if (
                    errorKey.startsWith('ORDER_PALLETS') ||
                    errorKey.includes('_COUNT') ||
                    errorKey.includes('_WEIGHT') ||
                    errorKey.includes('_LENGTH') ||
                    errorKey.includes('_WIDTH') ||
                    errorKey.includes('_HEIGHT')
                ) {
                    delete newErrors[errorKey];
                }
            });
        } else if (errorKey) {
            newErrors[errorKey] = error;
        } else {
            newErrors[key] = error;
        }

        setEditData(newData);
        setEditErrors(newErrors);

        if (key === 'STATUS' && value === 'suspended' && editData.STATUS !== 'suspended') {
            openReleaseFromSuspendedDialog(newData.RELEASE_FROM_SUSPENDED_AT);
        }
    };

    const openReleaseFromSuspendedDialog = (initialValue?: DateType | null) => {
        setConfirmParams({
            title: translate('orderDetail.confirmReleaseFromSuspendedAt'),
            maxWidth: 'sm',
            hideCancel: true,
            customContent: (data: ReleaseFromSuspendedConfirmDataType, setData) => (
                <ReleaseFromSuspendedConfirm data={data} setData={setData} initialValue={initialValue} />
            ),
            action: (data?: ReleaseFromSuspendedConfirmDataType) => {
                setEditData((prev) => ({ ...prev, RELEASE_FROM_SUSPENDED_AT: data?.value ?? null }));
            },
        });
    };

    const handleCreateReturn = async () => {
        if (!idOrder) {
            return;
        }

        try {
            const response = await api('get', `api/secure/returns/by-order/${idOrder}`);
            const existingReturns = response.data.data;

            if (existingReturns && existingReturns.length > 0) {
                const rmaList = existingReturns.map((ret: any) => ret.RMA).join('\n');
                const eshopOrderId = data?.ESHOP_ORDER_ID || '';

                setConfirmParams({
                    title: translate('returnDetail.confirmReturnExists'),
                    content: translate('returnDetail.confirmReturnExistsText', {
                        orderId: idOrder,
                        eshopOrderId,
                        rmaList,
                    }),
                    action: () => {
                        navigate(`/orders/${idOrder}/returns/new/`);
                    },
                });
            } else {
                navigate(`/orders/${idOrder}/returns/new/`);
            }
        } catch (error) {
            console.error('Error checking existing returns:', error);
            // Continue with navigation even if check fails
            navigate(`/orders/${idOrder}/returns/new/`);
        }
    };

    const handleCreateClaim = async () => {
        if (!idOrder) {
            return;
        }

        try {
            const claims = await getOrderClaims(idOrder);
            if (!claims.length) {
                navigate(`/orders/${idOrder}/claims/new/`);
                return;
            }

            setConfirmParams(
                buildClaimExistsDialogParams({
                    orderId: idOrder,
                    eshopOrderId: data?.ESHOP_ORDER_ID || '',
                    claims,
                    canCreateMultiple: permissionCreateMultipleClaims,
                    onConfirm: () => {
                        navigate(`/orders/${idOrder}/claims/new/`);
                    },
                })
            );
        } catch (error) {
            console.error('Error checking existing claims:', error);
            navigate(`/orders/${idOrder}/claims/new/`);
        }
    };

    const handleSplit = async () => {
        if (!data || !idOrder) {
            return;
        }
        const orderWithProducts = await ensureOrderHasProducts(data, idOrder);
        if (data.ORDER_PRODUCTS === undefined) {
            dispatch(
                getOrderDetailProductsFetchSuccess({
                    ORDER_PRODUCTS: orderWithProducts.ORDER_PRODUCTS,
                    ORDER_PRODUCT_SETS: orderWithProducts.ORDER_PRODUCT_SETS,
                })
            );
            productsFetchedRef.current = true;
        }
        const { updateOrder, cloneOrder } = getSplitOrder(orderWithProducts);

        const callback = (succes: boolean) => {
            if (succes) {
                dispatch(createOrderDetailFetch({ data: cloneOrder, mode: 'split' }));
            }
        };

        dispatch(updateOrderDetailFetch({ idOrder, data: updateOrder, callback }));
    };

    const handleClone = async () => {
        if (!data || !idOrder) {
            return;
        }
        const orderWithProducts = await ensureOrderHasProducts(data, idOrder);
        if (data.ORDER_PRODUCTS === undefined) {
            dispatch(
                getOrderDetailProductsFetchSuccess({
                    ORDER_PRODUCTS: orderWithProducts.ORDER_PRODUCTS,
                    ORDER_PRODUCT_SETS: orderWithProducts.ORDER_PRODUCT_SETS,
                })
            );
            productsFetchedRef.current = true;
        }

        const orderWithFiles = await ensureOrderHasFiles(orderWithProducts, idOrder);
        if (orderWithProducts.FILES === undefined) {
            filesFetchedRef.current = true;
            setEditData((prev) => ({ ...prev, FILES: orderWithFiles.FILES }));
        }

        let setsForClone = productsForSets;
        if (orderWithFiles.ORDER_PRODUCT_SETS?.length && !setsForClone.length) {
            setsForClone = await resolveProductsForSets(orderWithFiles.ORDER_PRODUCT_SETS);
        }

        const cloneData = await getCloneOrder(orderWithFiles, setsForClone);
        // Only customized sets keep child lines (nested under the set row); others are flat catalog lines.
        setProductsForSets(getCloneProductsForSets(orderWithFiles, setsForClone));
        setThisStorageData(cloneData);
        setEditData(cloneData);
        setEditErrors({});
        setActiveTab(INIT_TAB);
        navigate(URL_NEW);
    };

    const handleReorderShipping = () => {
        if (!idOrder) {
            return;
        }
        const callback = (succes: boolean) => {
            if (succes) {
                filesFetchedRef.current = true;
                dispatch(getOrderDetailFetch(idOrder));
                dispatch(getOrderDetailFilesFetch(idOrder));
            }
        };
        dispatch(reorderOrderShippingFetch({ order: data, callback }));
    };

    const handleCreateInvoice = () => {
        dispatch(createInvoiceOrderDetailFetch(idOrder));
    };

    const handleChangeTab = (newTab: string, requireValidation?: boolean) => {
        if (requireValidation) {
            const newFormErrors = checkFormValidity(formRef);
            if (activeTab === 'products' && !editData.ORDER_PRODUCTS?.length) {
                newFormErrors.ORDER_PRODUCTS = true;
            }
            if (!Object.keys(newFormErrors).length) {
                setActiveTab(newTab);
            } else {
                dispatch(
                    addMessage({
                        type: 'warning',
                        title: translate('messages.formWarningTitle'),
                        text: translate('messages.formWarningText'),
                    })
                );
            }
            setEditErrors(newFormErrors);
        } else {
            setActiveTab(newTab);
        }
    };

    const actionBusy = Boolean(loading || loadingUpdate);

    const branchIdValue = editData.BRANCH_ID != null ? String(editData.BRANCH_ID).trim() : '';
    const hasBranchId = Boolean(branchIdValue);

    const confirmBranchIdThen = (onKeep: () => void, onRemove?: () => void) => {
        const canRemove = !disableEdit && Boolean(onRemove);
        setConfirmParams({
            title: translate('orderDetail.confirmBranchIdTitle', { id: branchIdValue }),
            content: translate('orderDetail.confirmBranchIdText'),
            hideCancel: !canRemove,
            cancelLabel: canRemove ? translate('orderDetail.removeBranchId', { id: branchIdValue }) : undefined,
            onCancel: canRemove ? onRemove : undefined,
            action: onKeep,
            okLabel: 'confirmDialog.ok',
            dataTestId: 'order-branch-id-dialog',
        });
    };

    const handleSave = () => {
        if (actionBusy) {
            return;
        }
        if (disableEdit && !canEditAnyOrderPropertyInCurrentStatus) {
            const saveEshopNote = () =>
                dispatch(editOrderDetailEshopNoteFetch({ idOrder, data: { ESHOP_NOTE: editData.ESHOP_NOTE || '' } }));
            if (hasBranchId) {
                confirmBranchIdThen(saveEshopNote);
                return;
            }
            saveEshopNote();
            return;
        }
        if (!newOrder && data?.STATUS === 'canceled' && idOrder) {
            dispatch(
                patchOrderDetailFetch({
                    idOrder,
                    data: buildOrderCancelPatchPayload(idOrder, editData.CANCEL_REASON),
                })
            );
            return;
        }
        if (isCancelStatusChange(editData.STATUS, newOrder, ORDER_CANCEL_STATUS) && idOrder) {
            if (
                isOrderCancelReasonRequired(
                    data?.STATUS,
                    editData.STATUS,
                    data?.CANCEL_REASON,
                    permissionEditAll
                ) &&
                isEmptyCancelReason(editData.CANCEL_REASON)
            ) {
                setEditErrors({ CANCEL_REASON: true });
                dispatch(
                    addMessage({
                        type: 'warning',
                        title: translate('messages.formWarningTitle'),
                        text: translate('messages.formWarningText'),
                    })
                );
                return;
            }
            dispatch(
                patchOrderDetailFetch({
                    idOrder,
                    data: buildOrderCancelPatchPayload(idOrder, editData.CANCEL_REASON),
                })
            );
            return;
        }
        if (formRef.current) {
            const inputsList = Array.prototype.slice.call(formRef.current.getElementsByTagName('input'));
            const found = inputsList.find((el: any) => el.name === 'PAYMENT_METHOD_PAID');
            if (found && found.value.includes('null:')) {
                found.value = null;
            }
        }
        const newFormErrors = checkFormValidity(formRef);
        // Re-validate the required expedition date against current rules (a locked/unchanged stored date must not block saving)
        const otherEditErrors = { ...editErrors };
        delete otherEditErrors.REQUIRED_EXPEDITION_DATE;
        const checkedEditErrors: FormErrorsType =
            editData.ORDER_TYPE?.toLowerCase() === 'b2b' && editData.STATUS !== 'preOrder'
                ? {
                      ...otherEditErrors,
                      REQUIRED_EXPEDITION_DATE: getRequiredExpeditionDateError(
                          editData.REQUIRED_EXPEDITION_DATE,
                          data?.REQUIRED_EXPEDITION_DATE,
                          {
                              editable: isRequiredExpeditionDateEditable(editData.STATUS || 'preReceived', disableEdit, false),
                              now: moment(),
                          }
                      ),
                  }
                : otherEditErrors;
        if (!Object.keys(newFormErrors).length && !Object.values(checkedEditErrors).filter((value) => value).length) {
            const formatData = buildOrderSavePayload(editData, {
                productsLoaded: newOrder || editData.ORDER_PRODUCTS !== undefined,
                filesLoaded: newOrder || filesFetchedRef.current,
                sameRecipientBilling,
            });

            const saveCallback = (success: boolean) => {
                if (success && !newOrder && orderUsesPalletsTab(formatData)) {
                    dispatch(getOrderPalletsFetch(idOrder));
                }
            };

            const persistOrder = (payload: typeof formatData) => {
                if (newOrder) {
                    dispatch(createOrderDetailFetch({ data: payload }));
                } else {
                    dispatch(updateOrderDetailFetch({ idOrder, data: payload, callback: saveCallback }));
                }
            };

            if (hasBranchId) {
                confirmBranchIdThen(
                    () => persistOrder(formatData),
                    () => persistOrder({ ...formatData, BRANCH_ID: null })
                );
                setEditErrors({ ...newFormErrors, ...checkedEditErrors });
                return;
            }

            persistOrder(formatData);
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors({ ...newFormErrors, ...checkedEditErrors });
    };

    return (
        <div data-testid="order-detail">
            <TopBlock
                data={data}
                idOrder={idOrder}
                newOrder={newOrder}
                actionBusy={actionBusy}
                disableEdit={disableEdit}
                disableCreateClaim={disableCreateClaim}
                disableCreateReturn={disableCreateReturn}
                disableSplit={disableSplit}
                handleSave={handleSave}
                handleCreateReturn={handleCreateReturn}
                handleCreateClaim={handleCreateClaim}
                handleSplit={handleSplit}
                handleClone={handleClone}
                handleReorderShipping={handleReorderShipping}
                handleCreateInvoice={handleCreateInvoice}
                activeTab={activeTab}
                handleChangeTab={handleChangeTab}
                permissionCreate={permissionCreate}
                permissionReorderShipping={permissionReorderShipping}
                permissionOrderPalletsList={permissionOrderPalletsList}
                permissionOrderTransportsList={permissionTransportList}
                skladonUser={skladonUser}
                handleRemoveUnavailableProducts={handleRemoveUnavailableProducts}
                showRemoveUnavailableProducts={showRemoveUnavailableProducts}
            />
            <>
                {(loading || loadingUpdate) && <Loading />}
                <RenderComponents
                    data={data}
                    actionBusy={actionBusy}
                    onChange={onChange}
                    allowOrderProductDetail={!!providerData?.ALLOW_ORDER_PRODUCT_DETAIL}
                    onChangeMultiple={onChangeMultiple}
                    editData={editData}
                    editErrors={editErrors}
                    newOrder={newOrder}
                    disableEdit={disableEdit}
                    permissionEditAll={permissionEditAll}
                    permissionManagerEdit={permissionManagerEdit}
                    permissionEditOrderBoxCount={Boolean(orderPropertyPermissions.ORDER_BOX_COUNT)}
                    disableEditPrintFiles={disableEditPrintFiles}
                    isAttributeEditDisabled={isAttributeEditDisabled}
                    disableEditAllowExpedition={disableEditAllowExpedition}
                    disablePaidActions={disablePaidActions}
                    allowedStatuses={allowedStatuses}
                    orderStatusCodes={orderStatusCodes}
                    boolNumberCodes={boolNumberCodes}
                    currencyCodes={currencyCodes}
                    paymentCodes={paymentCodes}
                    documentTypesCodes={documentTypesCodes}
                    providerData={providerData}
                    permissionOrderPalletsList={permissionOrderPalletsList}
                    shipperCodes={shipperCodes}
                    serviceCodes={serviceCodes}
                    orderProductCustomsTypeCodes={orderProductCustomsTypeCodes}
                    orderProductCustomizationType={orderProductCustomizationTypeCodes}
                    orderClaimReturnTypeCodes={orderClaimReturnTypeCodes}
                    claimStatusCodesObject={claimStatusCodesObject}
                    returnStatusCodesObject={returnStatusCodesObject}
                    providerServiceCodes={providerServiceCodes}
                    orderStatusCodesObject={orderStatusCodesObject}
                    paymentCodesObject={paymentCodesObject}
                    shipperCodesObject={shipperCodesObject}
                    serviceCodesObject={serviceCodesObject}
                    lineStateCodesObject={lineStateCodesObject}
                    orderProductCustomsTypeCodesObject={orderProductCustomsTypeCodesObject}
                    orderProductCustomizationTypeCodesObject={orderProductCustomizationTypeCodesObject}
                    boolNumberCodesObject={boolNumberCodesObject}
                    orderTypeCodesObject={orderTypeCodesObject}
                    sourceSysCodesObject={sourceSysCodesObject}
                    activeTab={activeTab}
                    changelog={changelog}
                    handleShowShipperLabel={(value: 'shipResponse' | 'shipReturnResponse' | 'shipSwapResponse') =>
                        setShowShipperLabel(value)
                    }
                    sameRecipientBilling={sameRecipientBilling}
                    setSameRecipientBilling={(value: boolean) => setSameRecipientBilling(value)}
                    onSetProviderCompany={onSetProviderCompany}
                    providerCompaniesData={providerCompaniesData}
                    formRef={formRef}
                    expeditionPackages={expeditionPackages}
                    transportHistory={transportHistory}
                    orderTransports={orderTransports}
                    claimsReturns={claimsReturns}
                    permissionOrderTransportsList={permissionTransportList}
                    canCreateOrderTransport={canCreateOrderTransport}
                    transportStatusCodesObject={transportStatusCodesObject}
                    handleChangeTab={handleChangeTab}
                    handleSave={handleSave}
                    incorrectMessage={incorrectMessage}
                    productsForSets={productsForSets}
                    onChangeProductsForSets={onChangeProductsForSets}
                    setOpenProductDetailId={setOpenProductDetailId}
                    orderCancelReasonCodes={orderCancelReasonCodes}
                    orderCancelReasonCodesObject={orderCancelReasonCodesObject}
                    skladonUser={skladonUser}
                    orderMarketplacesCodes={orderMarketplacesCodes}
                    orderMarketplacesCodesObject={orderMarketplacesCodesObject}
                    allowedOrderProductEdit={providerAllowedOrderProductsEdit}
                    permissionOrderPalletsCreate={permissionOrderPalletsCreate}
                    permissionOrderPalletsUpdate={permissionOrderPalletsUpdate}
                    canEditPalletsInCurrentStatus={canEditPalletsInCurrentStatus}
                    permissionEditAddress={permissionEditAddress}
                />
            </>
            {showShipperLabel && idOrder && (
                <ShipperLabel
                    idOrder={idOrder}
                    editData={editData}
                    handleHideShipperLabel={() => setShowShipperLabel(false)}
                    type={showShipperLabel}
                />
            )}

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                    hideCancel={confirmParams.hideCancel}
                    maxWidth={confirmParams.maxWidth || 'lg'}
                    fullWidth={true}
                    okLabel={confirmParams.okLabel}
                    cancelLabel={confirmParams.cancelLabel}
                    dataTestId={confirmParams.dataTestId}
                />
            )}

            {openProductDetailId && (
                <OrderProductDetail
                    editData={editData}
                    onClose={() => setOpenProductDetailId(null)}
                    openProductDetailId={openProductDetailId}
                    onChange={onChange}
                    disableEdit={disableEdit}
                    orderProductFileTypeCodes={orderProductFileTypeCodes}
                    orderProductCustomizationTypeCodes={orderProductCustomizationTypeCodes}
                    allowOrderFiles={permissionOrderProductFilesEdit}
                    skladonUser={skladonUser}
                />
            )}
        </div>
    );
};

export default OrderDetailContainer;
